// web/js/player-approval/approvalManager.ts
function getLinkId() {
    const w = window;
    return w.jsconnect?.link?.id ? String(w.jsconnect.link.id) : null;
}
function getCurrentUser() {
    const w = window;
    const u = w.jsconnect?.user;
    if (!u || !u.id)
        return null;
    return { id: Number(u.id), name: String(u.name ?? '') };
}
function mapResponseToProps(fileId, data, user, onApprovalChange) {
    const history = data.history.map(h => ({
        userId: h.user_id,
        userName: h.name,
        avatarUrl: h.avatar_url,
        approved: h.approved_status,
        comment: h.comment ?? '',
        date: h.date ?? (Date.now() / 1000),
    }));
    const remainingReviewers = data.users_to_approve.map(r => ({
        userId: r.id,
        userName: r.name,
        avatarUrl: r.avatar_url,
        isCurrentUser: r.id === user.id,
    }));
    const hasAlreadyReviewed = data.history.some(h => h.user_id === user.id);
    // `beforeApprovalChange` is intentionally not set — it is an optional hook
    // that callers can pass through `window.mountApprovalPanel` directly when
    // they need to gate submissions.
    return {
        fileId,
        workflowName: data.is_approval_workflow ? data.workflow_name : null,
        canApprove: data.user_can_approve,
        hasAlreadyReviewed,
        approveTitle: data.approve_button_text,
        rejectTitle: data.reject_button_text,
        userName: user.name,
        userId: user.id,
        history,
        remainingReviewers,
        onApprovalChange,
    };
}
let currentHandle = null;
let currentFileId = null;
let fetchGeneration = 0;
function unmountPanel() {
    if (currentHandle) {
        try {
            currentHandle.unmount();
        }
        catch (_) { }
        currentHandle = null;
    }
}
async function fetchAndMount(fileId) {
    const generation = ++fetchGeneration;
    currentFileId = fileId;
    const user = getCurrentUser();
    if (!user) {
        unmountPanel();
        return;
    }
    let data;
    try {
        const resp = await fetch(`/file/${fileId}/approval`);
        if (generation !== fetchGeneration)
            return; // stale
        if (!resp.ok) {
            unmountPanel();
            return;
        }
        data = await resp.json();
    }
    catch {
        if (generation !== fetchGeneration)
            return;
        unmountPanel();
        return;
    }
    if (generation !== fetchGeneration)
        return; // stale
    const shouldShow = data.is_approval_workflow || data.history.length > 0;
    if (!shouldShow) {
        unmountPanel();
        return;
    }
    const onApprovalChange = (_fid, _approved, newWorkflowId) => {
        if (newWorkflowId != null && currentFileId != null) {
            fetchAndMount(currentFileId);
        }
    };
    const props = mapResponseToProps(fileId, data, user, onApprovalChange);
    unmountPanel();
    if (typeof window.mountApprovalPanel === 'function') {
        currentHandle = window.mountApprovalPanel('player-approval-container', props);
    }
}
function onColumnChange(snapshot) {
    const linkId = getLinkId();
    const fileId = snapshot.activeFileId;
    if (linkId || !fileId) {
        unmountPanel();
        currentFileId = null;
        return;
    }
    if (fileId === currentFileId)
        return; // already fetching or mounted
    fetchAndMount(fileId);
}
export function initApprovalManager() {
    const w = window;
    const trySubscribe = () => {
        if (!w.playerColumns || typeof w.playerColumns.subscribe !== 'function')
            return;
        // Initial fetch
        const snap = w.playerColumns.getSnapshot();
        if (snap)
            onColumnChange(snap);
        // Subscribe to future changes
        w.playerColumns.subscribe(onColumnChange);
    };
    // If columns are already ready, subscribe now; otherwise wait for the event.
    if (w.playerColumns && typeof w.playerColumns.getSnapshot === 'function') {
        trySubscribe();
    }
    else {
        window.addEventListener('player-columns-ready', trySubscribe, { once: true });
    }
}
/**
 * Re-fetch and re-render the approval panel for the currently focused file.
 * No-op if no file is focused or if in a shared-link context.
 */
export function refreshApprovalPanel() {
    if (currentFileId != null) {
        fetchAndMount(currentFileId);
    }
}
// Expose globally so non-module JS (player_utils.js, player.php, etc.) can call these.
window.initApprovalManager = initApprovalManager;
window.refreshApprovalPanel = refreshApprovalPanel;
